/**
 * ConstantineRepoModal — Asset-drawer run-Constantine dialog.
 *
 * Design choice: routing is detected client-side from the existing integrations
 * cache (no /constantine/route round-trip). If the asset URL host matches an
 * active SCM integration we POST to /job/; otherwise we POST to /osint/submit.
 * Unknown hosts fall through to OSINT (not unavailable) — if the repo is
 * private and the user has no matching integration, the scan will fail at scan
 * time with a clear error. Users are informed via the banner.
 *
 * Guardrails in extractIntegrationHost:
 *  - Rejects non-URL values (bare workspace names, path-only strings) by
 *    requiring the parsed hostname to contain at least one dot. This prevents
 *    public-suffix attacks (e.g. 'com' → every *.com domain matches via
 *    endsWith) and silently-failing Bitbucket bare workspace names.
 *  - Bare Bitbucket / GitHub workspace names (acc.value = 'myworkspace') are
 *    handled by a special-case: treat the integration as covering bitbucket.org
 *    or github.com respectively when host extraction fails.
 */

import { useEffect, useRef, useState } from 'react';

import { Modal } from '@/components/Modal';
import { useAxios } from '@/hooks/useAxios';
import { useIntegration } from '@/hooks/useIntegration';
import { queryClient } from '@/queryclient';
import {
  DEFAULT_GOAL,
  Pipeline,
} from '@/sections/customerManagement/components/modals/osintConstantineConstants';
import { PipelineSelect } from '@/sections/customerManagement/components/modals/PipelineSelect';
import { Account, Asset, Job } from '@/types';
import { cn } from '@/utils/classname';

/** Maps a known SaaS SCM hostname to its integration member name. */
const SCM_HOST_TO_CLASS: Record<string, string> = {
  'github.com': 'github',
  'gitlab.com': 'gitlab',
  'bitbucket.org': 'bitbucket',
  'dev.azure.com': 'azuredevops',
};

/**
 * Extract the hostname from an integration account value (which may be a full
 * URL or a bare workspace/org name).
 *
 * Returns the lowercase hostname when:
 *   1. The value parses as a URL (prepend https:// if no scheme present).
 *   2. The resulting hostname is non-empty AND contains at least one dot.
 *
 * Returns null for bare labels like 'myworkspace', 'com', 'org', or
 * path-only strings like '/just/a/path' — these would be single-label
 * hostnames that would dangerously match via the .endsWith() subdomain check.
 */
function extractIntegrationHost(value: string): string | null {
  if (!value) return null;
  let urlToParse = value;
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    urlToParse = 'https://' + value;
  }
  let hostname: string;
  try {
    hostname = new URL(urlToParse).hostname.toLowerCase();
  } catch {
    return null;
  }
  // DNS-format validation: must have at least one dot (rejects single-label
  // junk like 'myworkspace', 'com', 'just', etc.).
  if (!hostname || !hostname.includes('.')) {
    return null;
  }
  return hostname;
}

/** Derive the SCM class for a known SaaS host, or null if unrecognised. */
function getScmClass(host: string): string | null {
  return SCM_HOST_TO_CLASS[host] ?? null;
}

type RouteKind = 'integrated' | 'osint';

interface RouteState {
  kind: RouteKind;
  /** The SCM integration member name ('github', 'gitlab', etc.) when integrated */
  scmClass: string;
}

/**
 * Determine the submit route for a given asset URL given the connected
 * integrations list.
 *
 * Priority:
 *  1. Integration host-match: exact hostname or subdomain of acc.value host.
 *  2. Bare-name special-cases: Bitbucket / GitHub integrations whose acc.value
 *     has no parseable host (bare workspace name) are treated as covering
 *     bitbucket.org / github.com respectively.
 *  3. Known SaaS host (no integration configured) → OSINT.
 *  4. Unknown host → OSINT.
 */
function detectScmRoute(
  assetGroup: string,
  connectedIntegrations: Account[]
): RouteState {
  let assetHost: string;
  try {
    assetHost = new URL(assetGroup).hostname.toLowerCase();
  } catch {
    return { kind: 'osint', scmClass: 'unknown' };
  }

  // Pass 1: match via acc.value host (exact or subdomain).
  for (const acc of connectedIntegrations) {
    if (!acc.value) continue;
    const accHost = extractIntegrationHost(acc.value);
    if (!accHost) continue; // bare name — handled in pass 2
    if (assetHost === accHost || assetHost.endsWith('.' + accHost)) {
      return { kind: 'integrated', scmClass: acc.member };
    }
  }

  // Pass 2: bare-name special-cases for Bitbucket and GitHub.
  // When acc.value is a workspace/org name without a URL host, the integration
  // covers the canonical SaaS host.
  for (const acc of connectedIntegrations) {
    if (!acc.value) continue;
    const accHost = extractIntegrationHost(acc.value);
    if (accHost !== null) continue; // already handled in pass 1
    if (acc.member === 'bitbucket' && assetHost === 'bitbucket.org') {
      return { kind: 'integrated', scmClass: 'bitbucket' };
    }
    if (acc.member === 'github' && assetHost === 'github.com') {
      return { kind: 'integrated', scmClass: 'github' };
    }
  }

  // No integration matched — OSINT path.
  const saasClass = getScmClass(assetHost);
  return { kind: 'osint', scmClass: saasClass ?? 'unknown' };
}

interface ConstantineRepoModalProps {
  open: boolean;
  onClose: () => void;
  asset: Asset | null;
}

export function ConstantineRepoModal({
  open,
  onClose,
  asset,
}: ConstantineRepoModalProps) {
  const axios = useAxios();
  const [goal, setGoal] = useState('');
  const [pipeline, setPipeline] = useState<Pipeline>('premium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Load integrations from the existing cache (no extra network round-trip).
  const { data: integrationData, status: integrationStatus } = useIntegration();

  // Compute client-side route detection whenever the modal opens or integrations load.
  const routeState: RouteState | null = (() => {
    if (!open || !asset?.group) return null;
    return detectScmRoute(
      asset.group,
      integrationData?.connectedIntegrations ?? []
    );
  })();

  useEffect(() => {
    if (open) {
      setGoal('');
      setPipeline('premium');
      setError('');
      setSuccess('');
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSubmit = async () => {
    if (!asset || !routeState) return;
    setIsSubmitting(true);
    setError('');
    try {
      const resolvedGoal = goal.trim() || DEFAULT_GOAL;

      if (routeState.kind === 'integrated') {
        // Integrated path: dispatch via generic /job/ endpoint (same pattern as useGenerateCve).
        await axios.post<Job[]>('/job/', {
          assetKey: asset.key,
          capabilities: ['constantine'],
          config: {
            manual: 'true',
            pipeline,
            goal: resolvedGoal,
          },
        });
      } else {
        // Public / OSINT path: dispatch via /osint/submit (same as Technology-drawer modal).
        await axios.post('/osint/submit', {
          repo_url: asset.group,
          goal: resolvedGoal,
          pipeline,
        });
      }

      // Invalidate all MY job queries (covers both integrated and OSINT paths)
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'job';
        },
        refetchType: 'active',
      });

      const routeLabel =
        routeState.kind === 'integrated'
          ? `${routeState.scmClass} integration`
          : 'OSINT scan';
      setSuccess(
        `Constantine scan initiated via ${routeLabel}. Check the jobs tab.`
      );
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setGoal('');
      setPipeline('premium');
      setError('');
      setSuccess('');
      onClose();
    }
  };

  const isDetecting = integrationStatus === 'pending';

  return (
    <Modal
      size="xl"
      title="Research with Constantine"
      subtitle={`Hunt for 0-day vulnerabilities in ${asset?.group ?? ''}`}
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isSubmitting ? 'Submitting...' : 'Make Me Famous',
        onClick: handleSubmit,
        disabled: isDetecting || isSubmitting || !!success || !routeState,
      }}
    >
      <div className="space-y-4">
        {/* Route detection banner */}
        {!isDetecting && routeState?.kind === 'integrated' && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            Will use your{' '}
            {routeState.scmClass.charAt(0).toUpperCase() +
              routeState.scmClass.slice(1)}{' '}
            integration
          </div>
        )}

        {!isDetecting && routeState?.kind === 'osint' && (
          <div
            className="bg-chariot-background-secondary text-chariot-text-secondary rounded-md p-2 text-sm"
            role="status"
          >
            {routeState.scmClass !== 'unknown'
              ? `OSINT scan on ${routeState.scmClass.charAt(0).toUpperCase() + routeState.scmClass.slice(1)} — if your repo is private, configure a ${routeState.scmClass.charAt(0).toUpperCase() + routeState.scmClass.slice(1)} integration first.`
              : 'OSINT scan — if your repo is private, configure an SCM integration first.'}
          </div>
        )}

        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        <PipelineSelect
          value={pipeline}
          onValueChange={setPipeline}
          disabled={isSubmitting || !!success}
        />

        <div>
          <label
            htmlFor="constantine-repo-goal"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Research Goal
          </label>
          <textarea
            id="constantine-repo-goal"
            rows={8}
            value={goal}
            onChange={e => setGoal(e.target.value)}
            placeholder={DEFAULT_GOAL}
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'resize-y'
            )}
          />
          {!goal.trim() && (
            <button
              type="button"
              onClick={() => setGoal(DEFAULT_GOAL)}
              className="text-chariot-layer-primary mt-1 text-xs hover:underline"
            >
              Use default prompt
            </button>
          )}
          <p className="text-chariot-text-secondary mt-1 text-xs">
            {!goal.trim()
              ? 'Describe what vulnerabilities to hunt for, or use the default prompt above.'
              : goal === DEFAULT_GOAL
                ? 'Default prompt targets critical 0-day vulnerabilities.'
                : 'Your custom goal will focus the analysis.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
